import type { ReactElement, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ContentLibrary, ProductEducation } from '@shared/types';
import { getContentLibrary } from '../api/client';

export interface ContentLibraryStore {
  library: ContentLibrary;
  products: { productId: string; name: string }[];
  /** Display name for a catalog product, so a block never has to guess one. */
  productName: (productId: string) => string | null;
  /** The library's education module for a product, or null when it has none. */
  education: (productId: string) => ProductEducation | null;
}

const EMPTY_LIBRARY: ContentLibrary = {
  version: 0,
  staticSections: {},
  productEducation: {},
  familyEducation: {} as ContentLibrary['familyEducation'],
};

export function buildContentLibraryStore(
  library: ContentLibrary,
  products: { productId: string; name: string }[],
): ContentLibraryStore {
  return {
    library,
    products,
    productName: (productId) => products.find((p) => p.productId === productId)?.name ?? null,
    education: (productId) => library.productEducation[productId] ?? null,
  };
}

const DEFAULT_STORE = buildContentLibraryStore(EMPTY_LIBRARY, []);

export const ContentLibraryContext = createContext<ContentLibraryStore>(DEFAULT_STORE);

export function useContentLibrary(): ContentLibraryStore {
  return useContext(ContentLibraryContext);
}

/**
 * Fetches `GET /api/content-library` once per app load. The route may not
 * exist yet (pre-merge dev, or Stream F not deployed here) — a failed fetch
 * just leaves the library empty, and every block that reads from it already
 * falls back to its own placeholder copy.
 */
export function ContentLibraryProvider({ children }: { children: ReactNode }): ReactElement {
  const [library, setLibrary] = useState<ContentLibrary>(EMPTY_LIBRARY);
  const [products, setProducts] = useState<{ productId: string; name: string }[]>([]);

  useEffect(() => {
    let live = true;
    void getContentLibrary()
      .then((res) => {
        if (!live) return;
        setLibrary(res.library);
        setProducts(res.products);
      })
      .catch(() => {
        // Absent or unreachable endpoint: stay on the empty library.
      });
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo(() => buildContentLibraryStore(library, products), [library, products]);

  return <ContentLibraryContext.Provider value={value}>{children}</ContentLibraryContext.Provider>;
}
